import React from 'react'
import { Head } from '@inertiajs/react'
import { Edit3, Trash2, Plus, Check, X } from 'lucide-react'
import { AdminLayout } from '@/components/admin/templates/AdminLayout'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { Grid } from '@/components/admin/atoms/layout/Grid'
import { ScrollArea } from '@/components/admin/atoms/layout/ScrollArea'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { Text } from '@/components/admin/atoms/typography/Text'
import { Muted } from '@/components/admin/atoms/typography/Muted'
import { Badge } from '@/components/admin/atoms/feedback/Badge'
import { EmptyState } from '@/components/admin/atoms/feedback/EmptyState'
import { ActionButton } from '@/components/admin/atoms/actions/ActionButton'
import { IconButton } from '@/components/admin/atoms/actions/IconButton'
import { TextInput } from '@/components/admin/atoms/inputs/TextInput'
import { TextareaInput } from '@/components/admin/atoms/inputs/TextareaInput'
import { FormField } from '@/components/admin/molecules/form/FormField'
import { FormSection } from '@/components/admin/molecules/form/FormSection'
import { ConfirmDialog } from '@/components/admin/organisms/ConfirmDialog'
import { Divider } from '@/components/admin/atoms/layout/Divider'
import { DataTable } from '@/components/admin/atoms/data/DataTable'
import { DataTableHead } from '@/components/admin/atoms/data/DataTableHead'
import { DataTableHeaderCell } from '@/components/admin/atoms/data/DataTableHeaderCell'
import { DataTableBody } from '@/components/admin/atoms/data/DataTableBody'
import { DataTableRow } from '@/components/admin/atoms/data/DataTableRow'
import { DataTableCell } from '@/components/admin/atoms/data/DataTableCell'
import { useCategoriesIndexVM } from './Index.vm'
import type { PageProps } from '@/types/inertia.d'
import type { Category } from '@/types/admin'

type Props = PageProps<{ categories: Category[] }>

const CategoriesIndex: React.FC<Props> = ({ categories: initialCategories }) => {
  const vm = useCategoriesIndexVM(initialCategories)

  return (
    <AdminLayout breadcrumbs={[{ label: 'Categories' }]}>
      <Head title="Categories" />
      <Stack gap="6">
        <Heading level={2}>Categories</Heading>

        <Grid cols="2" gap="6">
          <FormSection>
            <Inline justify="between" align="center">
              <Text size="sm" weight="semibold">All Categories</Text>
              <Muted>{vm.categories.length} total</Muted>
            </Inline>
            <Divider />

            {vm.categories.length === 0 ? (
              <EmptyState title="No categories" description="Create your first category." />
            ) : (
              <ScrollArea horizontal>
                <DataTable>
                  <DataTableHead>
                    <DataTableHeaderCell>Name</DataTableHeaderCell>
                    <DataTableHeaderCell>Slug</DataTableHeaderCell>
                    <DataTableHeaderCell align="center">Posts</DataTableHeaderCell>
                    <DataTableHeaderCell width="80px" />
                  </DataTableHead>
                  <DataTableBody>
                    {vm.categories.map((cat) => (
                      <DataTableRow key={cat.id}>
                        {vm.isEditing(cat.id) ? (
                          <>
                            <DataTableCell>
                              <TextInput value={vm.editName} onChange={vm.setEditName} />
                            </DataTableCell>
                            <DataTableCell>
                              <TextInput value={vm.editSlug} onChange={vm.setEditSlug} />
                            </DataTableCell>
                            <DataTableCell align="center">
                              <Muted>{cat.posts_count ?? 0}</Muted>
                            </DataTableCell>
                            <DataTableCell>
                              <Inline gap="1">
                                <IconButton icon={<Check style={{ width: 16, height: 16 }} />} label="Save" variant="outline" onClick={vm.handleUpdate} disabled={vm.saving} />
                                <IconButton icon={<X style={{ width: 16, height: 16 }} />} label="Cancel" onClick={vm.cancelEdit} />
                              </Inline>
                            </DataTableCell>
                          </>
                        ) : (
                          <>
                            <DataTableCell>
                              <Stack gap="0">
                                <Text size="sm" weight="medium">{cat.name}</Text>
                                {cat.description && <Muted>{cat.description}</Muted>}
                              </Stack>
                            </DataTableCell>
                            <DataTableCell>
                              <Muted>{cat.slug}</Muted>
                            </DataTableCell>
                            <DataTableCell align="center">
                              <Badge variant="ghost">{cat.posts_count ?? 0}</Badge>
                            </DataTableCell>
                            <DataTableCell>
                              <Inline gap="1">
                                <IconButton icon={<Edit3 style={{ width: 16, height: 16 }} />} label="Edit" onClick={() => vm.startEdit(cat)} />
                                <IconButton icon={<Trash2 style={{ width: 16, height: 16 }} />} label="Delete" variant="error" onClick={() => vm.setDeleteId(cat.id)} />
                              </Inline>
                            </DataTableCell>
                          </>
                        )}
                      </DataTableRow>
                    ))}
                  </DataTableBody>
                </DataTable>
              </ScrollArea>
            )}
          </FormSection>

          <FormSection>
            <Text size="sm" weight="semibold">Add Category</Text>
            <Divider />
            <FormField label="Name" htmlFor="cat_name" required error={vm.createError || undefined}>
              <TextInput id="cat_name" value={vm.newName} onChange={vm.handleNewNameChange} placeholder="Category name" />
            </FormField>
            <FormField label="Slug" htmlFor="cat_slug">
              <TextInput id="cat_slug" value={vm.newSlug} onChange={vm.setNewSlug} placeholder="category-slug" />
            </FormField>
            <FormField label="Description" htmlFor="cat_description">
              <TextareaInput id="cat_description" value={vm.newDescription} onChange={vm.setNewDescription} placeholder="Optional description…" rows={3} />
            </FormField>
            <ActionButton variant="primary" onClick={vm.handleCreate} loading={vm.creating} disabled={vm.createDisabled}>
              <Plus style={{ width: 16, height: 16 }} />
              Save Category
            </ActionButton>
          </FormSection>
        </Grid>
      </Stack>

      <ConfirmDialog
        open={vm.deleteDialogOpen}
        title="Delete Category"
        description="Posts in this category will have their category removed. This action cannot be undone."
        onConfirm={vm.handleDelete}
        onCancel={() => vm.setDeleteId(null)}
        loading={vm.deleting}
      />
    </AdminLayout>
  )
}

export default CategoriesIndex
